import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  APP_NAME,
  APP_TAGLINE,
  APP_DESCRIPTION,
  APP_FEATURES,
  APP_AUDIENCE,
  POWERED_BY,
} from '../../legal/appMarketingConstants';
import {
  HERO_SLIDES,
  HERO_TYPE_LINES,
  AGENDA_ITEMS,
  SERVICES,
  FEATURE_GROUPS,
  TEAM,
} from './landingContent';
import { AppleIcon, WindowsIcon, DownloadIcon, PlayStoreIcon } from './PlatformIcons';
import { APP_STORE_URL, PLAY_STORE_URL, StoreBadgeRow } from './StoreBadges';
import { DESKTOP_RELEASES_URL } from '../../config/desktopDriveLinks';
import { fetchLatestAppUpdates, fileLabelFromUrl } from '../../config/appUpdates';
import './LandingPage.css';

const logoSrc = `${process.env.PUBLIC_URL || ''}/images/workloop-logo.png`;
const SLIDE_MS = 7000;
const TYPE_SPEED = 42;
const DELETE_SPEED = 28;
const HOLD_MS = 1800;

const LATEST_DOWNLOAD_META = {
  IOS: {
    platform: 'iOS',
    platformKey: 'ios',
    arch: 'iPhone & iPad',
    format: 'App Store',
    icon: AppleIcon,
    accent: '#111111',
    rowClass: 'wl-row--ios',
    cta: 'View on App Store',
    fallbackHref: APP_STORE_URL,
  },
  ANDROID: {
    platform: 'Android',
    platformKey: 'android',
    arch: 'Phone & tablet',
    format: 'Google Play',
    icon: PlayStoreIcon,
    accent: '#34A853',
    rowClass: 'wl-row--android',
    cta: 'View on Google Play',
    fallbackHref: PLAY_STORE_URL,
  },
  DESKTOP_MAC: {
    platform: 'macOS',
    platformKey: 'mac',
    arch: 'Apple Silicon / Intel',
    format: 'Desktop app',
    icon: AppleIcon,
    accent: '#6366f1',
    rowClass: 'wl-row--mac',
    cta: 'Download',
  },
  DESKTOP_WIN: {
    platform: 'Windows',
    platformKey: 'win',
    arch: 'x64',
    format: 'Desktop app',
    icon: WindowsIcon,
    accent: '#0ea5e9',
    rowClass: 'wl-row--win',
    cta: 'Download',
  },
};

const PLATFORM_CARDS = [
  { key: 'ios', apiKey: 'IOS', label: 'iOS', Icon: AppleIcon, color: '#111111', bg: 'rgba(17, 17, 17, 0.08)', note: 'iPhone & iPad on the App Store', delay: '0s' },
  { key: 'android', apiKey: 'ANDROID', label: 'Android', Icon: PlayStoreIcon, color: '#34A853', bg: 'rgba(52, 168, 83, 0.12)', note: 'Get it on Google Play', delay: '0.08s' },
  { key: 'mac', apiKey: 'DESKTOP_MAC', label: 'macOS', Icon: AppleIcon, color: '#6366f1', bg: 'rgba(99, 102, 241, 0.12)', note: 'Latest Mac desktop build', delay: '0.16s' },
  { key: 'win', apiKey: 'DESKTOP_WIN', label: 'Windows', Icon: WindowsIcon, color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.12)', note: 'Latest Windows desktop build', delay: '0.24s' },
];

function TypewriterLine({ lines }) {
  const [lineIndex, setLineIndex] = useState(0);
  const [text, setText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const full = lines[lineIndex] || '';
    let timeout;

    if (!deleting && text === full) {
      timeout = setTimeout(() => setDeleting(true), HOLD_MS);
    } else if (deleting && text === '') {
      setDeleting(false);
      setLineIndex((i) => (i + 1) % lines.length);
    } else {
      timeout = setTimeout(() => {
        const next = deleting
          ? full.slice(0, Math.max(0, text.length - 1))
          : full.slice(0, text.length + 1);
        setText(next);
      }, deleting ? DELETE_SPEED : TYPE_SPEED);
    }

    return () => clearTimeout(timeout);
  }, [deleting, lineIndex, lines, text]);

  return (
    <p className="wl-typewriter" aria-live="polite">
      <span className="wl-typewriter__text">{text}</span>
      <span className="wl-typewriter__caret" aria-hidden="true" />
    </p>
  );
}

function HeroSlider({ onGoToApp }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((idx) => {
    setActive((idx + HERO_SLIDES.length) % HERO_SLIDES.length);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => go(active + 1), SLIDE_MS);
    return () => clearInterval(t);
  }, [active, paused, go]);

  const slide = HERO_SLIDES[active];

  return (
    <section
      className="wl-hero-slider wl-hero-slider--split"
      style={{ '--slide-accent': slide.accent, '--slide-gradient': slide.gradient }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="WorkLoop highlights"
    >
      <div className="wl-hero-split">
        <div className="wl-hero-split__left">
          <p className="wl-hero-slide__kicker">{slide.kicker}</p>
          <h1 className="wl-hero-slide__brand">
            <span className="wl-hero-slide__brand-glow">WorkLoop</span>
          </h1>
          <TypewriterLine lines={HERO_TYPE_LINES} />
          <p className="wl-hero-split__lede">{slide.subtitle}</p>
          <div className="wl-hero-slider__actions">
            <a href={slide.ctaHref} className="wl-btn wl-btn--primary wl-btn--shine">
              {slide.cta}
            </a>
            <button type="button" className="wl-btn wl-btn--outline" onClick={onGoToApp}>
              Go to app
            </button>
          </div>
          <StoreBadgeRow className="wl-store-badges--hero" />
        </div>

        <div className="wl-hero-split__right">
          <div className="wl-hero-orbit" aria-hidden="true">
            <span className="wl-hero-orbit__ring" />
            <span className="wl-hero-orbit__ring wl-hero-orbit__ring--2" />
            <span className="wl-hero-orbit__dot wl-hero-orbit__dot--1" />
            <span className="wl-hero-orbit__dot wl-hero-orbit__dot--2" />
            <span className="wl-hero-orbit__dot wl-hero-orbit__dot--3" />
          </div>
          <div className="wl-hero-slider__track">
            {HERO_SLIDES.map((s, i) => (
              <article
                key={s.id}
                className={`wl-hero-slide ${i === active ? 'is-active' : ''}`}
                aria-hidden={i !== active}
              >
                <h2 className="wl-hero-slide__title">{s.headline}</h2>
                <ul className="wl-hero-panel__points">
                  {(s.points || []).map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </div>

      <div className="wl-hero-slider__controls">
        <button type="button" className="wl-hero-slider__arrow" onClick={() => go(active - 1)} aria-label="Previous slide">
          ‹
        </button>
        <div className="wl-hero-slider__dots" role="tablist">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`Slide ${i + 1}: ${s.headline}`}
              className={`wl-hero-slider__dot ${i === active ? 'is-active' : ''}`}
              onClick={() => go(i)}
            />
          ))}
        </div>
        <button type="button" className="wl-hero-slider__arrow" onClick={() => go(active + 1)} aria-label="Next slide">
          ›
        </button>
      </div>

      <div className="wl-hero-slider__progress" aria-hidden="true">
        <span
          key={active}
          className="wl-hero-slider__progress-bar"
          style={{ animationDuration: `${SLIDE_MS}ms`, animationPlayState: paused ? 'paused' : 'running' }}
        />
      </div>
    </section>
  );
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [latestApps, setLatestApps] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchLatestAppUpdates().then((rows) => {
      if (!cancelled) setLatestApps(rows);
    });
    return () => { cancelled = true; };
  }, []);

  const goToApp = () => {
    if (isAuthenticated) {
      navigate(user?.role === 'ADMIN' ? '/admin' : '/chat');
      return;
    }
    navigate('/login');
  };

  const closeMenu = () => setMenuOpen(false);

  const latestByPlatform = latestApps.reduce((acc, item) => {
    if (item?.platform) acc[item.platform] = item;
    return acc;
  }, {});

  const downloadRows = ['IOS', 'ANDROID', 'DESKTOP_MAC', 'DESKTOP_WIN']
    .map((key) => {
      const meta = LATEST_DOWNLOAD_META[key];
      const latest = latestByPlatform[key];
      const href = latest?.downloadUrl || meta.fallbackHref;
      if (!meta || !href) return null;
      const version = latest?.latestVersionName || null;
      return {
        id: key,
        ...meta,
        href,
        version,
        file: fileLabelFromUrl(href, version),
        size: version ? `v${version}` : (key === 'IOS' || key === 'ANDROID' ? 'Store' : 'Latest'),
        fromAdmin: Boolean(latest?.fromAdmin),
      };
    })
    .filter(Boolean);

  return (
    <div className="wl-landing">
      <div className="wl-bg-blobs" aria-hidden="true">
        <span className="wl-blob wl-blob--1" />
        <span className="wl-blob wl-blob--2" />
        <span className="wl-blob wl-blob--3" />
      </div>

      <header className={`wl-nav ${scrolled ? 'wl-nav--solid' : ''}`}>
        <div className="wl-nav__inner">
          <a href="#top" className="wl-nav__brand" onClick={closeMenu}>
            <img src={logoSrc} alt="" className="wl-nav__logo" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="wl-nav__brand-text">{APP_NAME}</span>
          </a>
          <button type="button" className="wl-nav__toggle" aria-expanded={menuOpen} aria-label="Toggle menu" onClick={() => setMenuOpen((v) => !v)}>
            <span /><span /><span />
          </button>
          <nav className={`wl-nav__links ${menuOpen ? 'is-open' : ''}`}>
            <a href="#agenda" onClick={closeMenu}>Why WorkLoop</a>
            <a href="#services" onClick={closeMenu}>Services</a>
            <a href="#features" onClick={closeMenu}>Features</a>
            <a href="#download" onClick={closeMenu}>Download</a>
            <a href="#about" onClick={closeMenu}>About team</a>
            <button type="button" className="wl-nav__cta" onClick={() => { closeMenu(); goToApp(); }}>Go to app</button>
          </nav>
        </div>
      </header>

      <main id="top">
        {/* Brand strip */}
        <div className="wl-brand-strip wl-fade-up">
          <img src={logoSrc} alt="" className="wl-brand-strip__logo" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <p className="wl-brand-strip__name"><span className="wl-gradient-text">{APP_NAME}</span></p>
            <p className="wl-brand-strip__tag">{APP_TAGLINE}</p>
          </div>
        </div>

        <HeroSlider onGoToApp={goToApp} />

        {/* Agenda / Why */}
        <section id="agenda" className="wl-section wl-section--white">
          <div className="wl-section__inner">
            <div className="wl-section__head wl-section__head--center">
              <p className="wl-kicker">Our agenda</p>
              <h2>Why teams choose WorkLoop</h2>
              <p>{APP_DESCRIPTION}</p>
            </div>
            <div className="wl-agenda-grid">
              {AGENDA_ITEMS.map((item, i) => (
                <article key={item.title} className="wl-agenda-card wl-fade-up" style={{ '--delay': `${0.08 * i}s` }}>
                  <span className="wl-agenda-card__icon">{item.icon}</span>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section id="services" className="wl-section">
          <div className="wl-section__inner">
            <div className="wl-section__head wl-section__head--center">
              <p className="wl-kicker">Services</p>
              <h2>Everything your team needs in one place</h2>
              <p>From daily chat to attendance and desktop sync — WorkLoop covers the full workday.</p>
            </div>
            <div className="wl-services-grid">
              {SERVICES.map((svc, i) => (
                <article
                  key={svc.title}
                  className="wl-service-card wl-fade-up"
                  style={{ '--svc-color': svc.color, '--delay': `${0.06 * i}s` }}
                >
                  <div className="wl-service-card__bar" />
                  <h3>{svc.title}</h3>
                  <p className="wl-service-card__summary">{svc.summary}</p>
                  <ul>
                    {svc.points.map((pt) => (
                      <li key={pt}>{pt}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="wl-section wl-section--white">
          <div className="wl-section__inner">
            <div className="wl-section__head wl-section__head--center">
              <p className="wl-kicker">Features</p>
              <h2>Built for how teams actually work</h2>
              <p>{APP_AUDIENCE}</p>
            </div>

            <div className="wl-features-highlight">
              {APP_FEATURES.map((f) => (
                <div key={f.title} className="wl-feature-pill">
                  <strong>{f.title}</strong>
                  <span>{f.description}</span>
                </div>
              ))}
            </div>

            <div className="wl-features-grid">
              {FEATURE_GROUPS.map((group, i) => (
                <article
                  key={group.name}
                  className="wl-feature-group wl-fade-up"
                  style={{ '--fg-color': group.color, '--delay': `${0.05 * i}s` }}
                >
                  <h3>{group.name}</h3>
                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Download */}
        <section id="download" className="wl-section wl-section--tint">
          <div className="wl-section__inner">
            <div className="wl-section__head wl-section__head--center">
              <p className="wl-kicker">Download</p>
              <h2>Apps for <span className="wl-gradient-text--alt">every device</span></h2>
              <p>
                Latest builds from Admin → App Updates. Version and download link update site-wide
                as soon as you save a platform config.
              </p>
            </div>
            <StoreBadgeRow className="wl-store-badges--section" />
            <div className="wl-platform-cards">
              {PLATFORM_CARDS.map(({ key, apiKey, label, Icon, color, bg, note, delay }) => {
                const latest = latestByPlatform[apiKey];
                const href = latest?.downloadUrl
                  || LATEST_DOWNLOAD_META[apiKey]?.fallbackHref
                  || null;
                const version = latest?.latestVersionName;
                const inner = (
                  <>
                    <div className="wl-platform-card__glow" />
                    <div className="wl-platform-card__icon-wrap"><Icon className="wl-platform-card__icon" /></div>
                    <h3>{label}</h3>
                    <p>{version ? `Latest ${version}` : note}</p>
                  </>
                );
                const cardClass = 'wl-platform-card wl-fade-up';
                const cardStyle = { '--card-color': color, '--card-bg': bg, '--delay': delay };
                return href ? (
                  <a
                    key={key}
                    href={href}
                    className={cardClass}
                    style={cardStyle}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Get WorkLoop for ${label}${version ? ` ${version}` : ''}`}
                  >
                    {inner}
                  </a>
                ) : (
                  <article key={key} className={cardClass} style={cardStyle}>
                    {inner}
                  </article>
                );
              })}
            </div>
            <div className="wl-table-wrap wl-fade-up">
              <table className="wl-download-table">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Architecture</th>
                    <th>Format</th>
                    <th>Version</th>
                    <th>File</th>
                    <th aria-label="Download" />
                  </tr>
                </thead>
                <tbody>
                  {downloadRows.map((row, index) => {
                    const Icon = row.icon;
                    return (
                      <tr
                        key={row.id}
                        className={row.rowClass}
                        style={{ '--row-delay': `${0.04 * index}s` }}
                      >
                        <td data-label="Platform">
                          <span className="wl-table-platform">
                            <span className="wl-table-platform__icon" style={{ color: row.accent }}><Icon /></span>
                            {row.platform}
                          </span>
                        </td>
                        <td data-label="Architecture">{row.arch}</td>
                        <td data-label="Format"><span className={`wl-format-badge wl-format-badge--${row.platformKey}`}>{row.format}</span></td>
                        <td data-label="Version">
                          {row.version ? (
                            <span className="wl-file-name">v{row.version}</span>
                          ) : (
                            <span className="wl-table-size">—</span>
                          )}
                        </td>
                        <td data-label="File"><code className="wl-file-name">{row.file}</code></td>
                        <td data-label="Download">
                          <a
                            href={row.href}
                            className={`wl-dl-btn wl-dl-btn--${row.platformKey}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.platformKey === 'ios' || row.platformKey === 'android' ? (
                              <Icon className="wl-dl-btn__icon" />
                            ) : (
                              <DownloadIcon className="wl-dl-btn__icon" />
                            )}
                            {row.cta}
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="wl-download-footnote">
              Looking for another Mac arch or Linux package? See all release assets on{' '}
              <a href={DESKTOP_RELEASES_URL} target="_blank" rel="noopener noreferrer" className="wl-inline-link">
                GitHub Releases
              </a>
              .
            </p>
            <p className="wl-download-footnote">
              If macOS says it could not verify WorkLoop: in Finder, <strong>Control-click</strong> the app, choose <strong>Open</strong>, then <strong>Open</strong> again. Or go to System Settings → Privacy &amp; Security → Open Anyway.
            </p>
            <p className="wl-download-footnote">
              Prefer the browser? <button type="button" className="wl-inline-link" onClick={goToApp}>Open WorkLoop in the web app</button> — no install required.
            </p>
          </div>
        </section>

        {/* About team */}
        <section id="about" className="wl-section">
          <div className="wl-section__inner">
            <div className="wl-section__head wl-section__head--center">
              <p className="wl-kicker">About team</p>
              <h2>People behind the loop</h2>
              <p>WorkLoop is built and supported by {POWERED_BY}.</p>
            </div>
            <div className="wl-team-grid">
              {TEAM.map((member, i) => (
                <article key={member.role} className="wl-team-card wl-fade-up" style={{ '--delay': `${0.1 * i}s` }}>
                  <p className="wl-team-card__role">{member.role}</p>
                  <h3>{member.name}</h3>
                  <p>{member.blurb}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="wl-cta-band">
          <div className="wl-cta-band__inner">
            <h2>Ready to connect your team?</h2>
            <p>Join channels, track tasks, and collaborate — on web, mobile, or desktop.</p>
            <div className="wl-cta-band__actions">
              <button type="button" className="wl-btn wl-btn--primary wl-btn--shine" onClick={goToApp}>Go to app</button>
              <a href="#download" className="wl-btn wl-btn--outline"><DownloadIcon className="wl-btn__icon" />Download</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="wl-footer">
        <div className="wl-footer__inner">
          <p>© {new Date().getFullYear()} {APP_NAME}. Powered by {POWERED_BY}.</p>
          <nav>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/support">Support</Link>
            <Link to="/login">Sign in</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
